'use client';

import cn from 'classnames';
import { ContentCardProps } from './ContentCard.type';
import { Text } from '@/ui-components/atoms/Text/Text';
import { LinkWrapperIfHref } from '@/ui-components/atoms/LinkWrapperIfHref/LinkWrapperIfHref';
import { AspectRatio } from '@/ui-components/atoms/AspectRatio/AspectRatio';
import { RatioBreakpoints } from '@/ui-components/atoms/AspectRatio/AspectRatio.type';
import styles from './contentcard.module.scss';
import { AudioPlayer } from '@/ui-components/atoms/AudioPlayer/AudioPlayer';
import { ChannelBadge } from '@/ui-components/molecules/ChannelCardStrip/ChannelBadge';
import { PropsWithChildren, useState } from 'react';
import { Button } from '@/ui-components/atoms/Button/Button';
import { ContentCardModal } from './ContentCardModal';

export default function ContentCard({
    image,
    imageSide = 'left',
    video,
    smallVerticalMedia,
    eyebrow,
    title,
    body,
    linkProps,
    theme = 'vertical',
    mobileLayout,
    mobileImage = 'leading',
    tabletLayout,
    copyright,
    audio,
    isTextCenter,
    channelLogo,
    isVODCard = false,
    isTruncated,
    titleText,
}: ContentCardProps): React.JSX.Element {
    const [isPlaying, setIsPlaying] = useState<boolean>(false);

    const hasInteractiveContent = !!(video || audio?.source || isTruncated);

    const resolvedTabletLayout = tabletLayout || theme;
    const wrapperClassNames = cn(styles.contentCard, {
        [styles.cardHover]: linkProps && linkProps.href,
        [styles.titleHover]: !hasInteractiveContent,
        [styles.imageRight]: imageSide === 'right',
        [styles.textCenter]: isTextCenter,
    });
    const mediaClassNames = cn(styles.mediaContainer, {
        [styles.smallVerticalMedia]: smallVerticalMedia,
    });

    const contentContainerClassNames = cn(styles.contentContainer, {
        [styles.titleHover]: hasInteractiveContent,
    });

    let ratioCols: RatioBreakpoints = { sm: '4-3', md: '16-9' };

    if (theme === 'horizontal' && mobileLayout === 'horizontal') {
        ratioCols = { sm: '4-3' };
    } else if (theme === 'horizontal' && smallVerticalMedia) {
        ratioCols = { sm: '4-3', md: '16-9' };
    }

    const hasContent =
        (!isVODCard && eyebrow) || title || body || (linkProps?.href && linkProps?.children) || audio?.source;

    const [readMoreModalOpen, setReadMoreModalOpen] = useState(false);

    const handleReadMoreClick = (e: React.MouseEvent) => {
        e.preventDefault();
        setReadMoreModalOpen(true);
    };

    const handleCloseReadMoreModal = () => {
        setReadMoreModalOpen(false);
    };

    const titleSection = title ? (
        !isTruncated ? (
            <Text tag="div" variant="h6" className={cn(styles.title, { [styles.truncated]: isTruncated })}>
                {title}
            </Text>
        ) : (
            <LinkWrapperIfHref link={linkProps} className={styles.cardLink}>
                <Text tag="div" variant="h6" className={cn(styles.title, { [styles.truncated]: isTruncated })}>
                    {title}
                </Text>
            </LinkWrapperIfHref>
        )
    ) : null;

    return (
        <>
            <CardWrapper hasInteractiveContent={hasInteractiveContent} linkProps={linkProps}>
                {/* data-layout-* props let CSS handle responsive layout fully on the server */}
                <div
                    className={wrapperClassNames}
                    data-layout-mobile={mobileLayout}
                    data-layout-tablet={resolvedTabletLayout}
                    data-layout-desktop={theme}
                    data-mobile-image={mobileImage}
                    data-cy="content-card"
                >
                    <div className={mediaClassNames}>
                        {(video || image) && (
                            <>
                                {!video && (
                                    <div className={styles.imageContainer}>
                                        {image}
                                        {channelLogo && <div className={styles.channelLogo}>{channelLogo}</div>}
                                    </div>
                                )}
                                {video && (
                                    <AspectRatio cols={ratioCols} className={styles.videoContainer}>
                                        {video}
                                    </AspectRatio>
                                )}
                                {copyright?.trim() && (
                                    <Text tag="div" variant="text-small" className={styles.copyright}>
                                        {copyright}
                                    </Text>
                                )}
                            </>
                        )}
                    </div>
                    {hasContent && (
                        <LinkWrapperIfHref
                            link={hasInteractiveContent && !isTruncated ? linkProps : undefined}
                            className={contentContainerClassNames}
                            forceContainer
                        >
                            {!isVODCard && eyebrow && <div className={styles.eyebrow}>{eyebrow}</div>}

                            {titleSection}

                            {isVODCard && eyebrow && (
                                <div className={styles.eyebrowWrapper}>
                                    <div className={cn(styles.eyebrow, styles.eyebrowWithBadge)}>{eyebrow}</div>
                                    <ChannelBadge badgeText="APP ONLY" className={styles.appOnlyBadge} />
                                </div>
                            )}
                            {body && (
                                <Text
                                    tag="div"
                                    className={cn(styles.body, {
                                        [styles.truncated]: isTruncated,
                                    })}
                                >
                                    {body}
                                </Text>
                            )}
                            {!isTruncated && linkProps && linkProps.href && linkProps.children && (
                                <span ref={undefined} className={styles.link}>
                                    {linkProps.children}
                                </span>
                            )}
                            {audio?.source && !linkProps?.href && theme !== 'horizontal' && (
                                <div
                                    className={cn(styles.audioPlayerWrapper, {
                                        [styles.withReadmore]: isTruncated,
                                    })}
                                >
                                    <AudioPlayer
                                        {...audio}
                                        isActive={isPlaying}
                                        onActivate={() => setIsPlaying(true)}
                                        name={typeof title === 'string' ? title : ''}
                                    />
                                </div>
                            )}
                            {isTruncated && (
                                <div className={styles.buttonWrapper}>
                                    <Button
                                        onClick={handleReadMoreClick}
                                        variant="tertiary"
                                        tertiaryNoPadding
                                        type="button"
                                        fitting="hug-contents"
                                        ariaLabel={
                                            titleText ? `Read more about ${titleText}` : 'Read more about this content'
                                        }
                                    >
                                        <span className={styles.contentText}>Read more</span>
                                    </Button>
                                </div>
                            )}
                        </LinkWrapperIfHref>
                    )}
                </div>
            </CardWrapper>
            {readMoreModalOpen && (
                <ContentCardModal
                    open={readMoreModalOpen}
                    onClose={handleCloseReadMoreModal}
                    dataLocalpagename={'overlay:contentcard'}
                    eyebrow={eyebrow}
                    title={title}
                    image={image}
                    video={video}
                    copyright={copyright}
                    body={body}
                    audio={audio}
                    linkProps={linkProps}
                    theme={theme}
                    mobileLayout={mobileLayout}
                    smallVerticalMedia={smallVerticalMedia}
                />
            )}
        </>
    );
}

function CardWrapper({
    linkProps,
    hasInteractiveContent,
    children,
}: PropsWithChildren<{
    children: React.ReactNode;
    hasInteractiveContent: boolean;
    linkProps?: ContentCardProps['linkProps'];
}>) {
    if (!hasInteractiveContent) {
        return (
            <LinkWrapperIfHref link={linkProps} className={styles.cardLink}>
                {children}
            </LinkWrapperIfHref>
        );
    }
    return <div className={styles.cardLink}>{children}</div>;
}
